import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useApiConfig, type MutationApiConfig } from "@visioni/a3s-client-api/app";
import type { ClientApiFeatureSubPathParams } from "@visioni/a3s-client-api";
import { recordActivityMutation } from "../queries";

const EMPTY_PATH_PARAMS: ClientApiFeatureSubPathParams = {};

/**
 * Generic hook for recording user activity (audit trail).
 *
 * Call `.mutate({ kind: "livestream_viewed" })` or
 * `.mutate({ kind: "clip_viewed", clipId: "..." })` to fire-and-forget
 * an audit record. The hook handles authentication and path-param wiring.
 */
export function useRecordActivity(pathParams: ClientApiFeatureSubPathParams | undefined) {
  const apiConfig = useApiConfig();

  const mutationConfig: MutationApiConfig = useMemo(
    () => ({
      tenantId: apiConfig.tenant?.tenantId,
      apiBaseUrl: apiConfig.tenant?.apiBaseUrl,
      authenticatedFetch: apiConfig.authenticatedFetch,
    }),
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const mutationOptions = useMemo(
    () => recordActivityMutation(mutationConfig, pathParams ?? EMPTY_PATH_PARAMS),
    [mutationConfig, pathParams],
  );

  return useMutation({
    ...mutationOptions,
    retry: false,
  });
}
